import { toast } from "sonner";
import { ArrowDown, ArrowUp, Trash2 } from "lucide-react";
import { api, errMsg, img } from "@/lib/api";
import { Switch } from "@/components/ui/switch";

const input = "h-10 w-full rounded-lg border border-white/10 bg-white/5 px-3 text-sm outline-none focus:border-[#0066FF]";
const POSITIONS = [["center", "Centro"], ["center top", "In alto"], ["center bottom", "In basso"], ["left center", "Sinistra"], ["right center", "Destra"]];

const ImagePick = ({ label, url, onChange, testid }) => {
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/admin/upload", fd);
      onChange(data.url);
    } catch (err) {
      toast.error(errMsg(err));
    }
    e.target.value = "";
  };
  return (
    <div>
      <p className="mb-1.5 text-xs text-slate-400">{label}</p>
      <label className="group relative flex aspect-video cursor-pointer items-center justify-center overflow-hidden rounded-xl border border-dashed border-white/20 bg-black/30 text-xs text-slate-400 hover:border-[#0066FF]">
        {url ? <img src={img(url)} alt="" className="absolute inset-0 h-full w-full object-cover" /> : "Carica immagine"}
        <span className="absolute bottom-2 right-2 rounded-full bg-black/70 px-2 py-1 text-[10px] text-white opacity-0 transition-opacity group-hover:opacity-100">Sostituisci</span>
        <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid={testid} />
      </label>
      {url && label.includes("smartphone") && <button onClick={() => onChange(null)} className="mt-1 text-[11px] text-slate-500 hover:text-red-400">Rimuovi</button>}
    </div>
  );
};

const Item = ({ it, i, n, kind, set, move, remove }) => (
  <div className="rounded-2xl border border-white/10 bg-[#12141A] p-5" data-testid={`${kind}-item-${i}`}>
    <div className="mb-4 flex items-center justify-between">
      <span className="font-mono text-xs text-slate-500">#{i + 1}</span>
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-2 text-xs text-slate-400">Attiva<Switch checked={!!it.active} onCheckedChange={(v) => set({ active: v })} data-testid={`${kind}-active-${i}`} /></label>
        <button disabled={i === 0} onClick={() => move(-1)} className="p-1.5 text-slate-400 hover:text-white disabled:opacity-30" aria-label="Sposta su" data-testid={`${kind}-up-${i}`}><ArrowUp className="h-4 w-4" /></button>
        <button disabled={i === n - 1} onClick={() => move(1)} className="p-1.5 text-slate-400 hover:text-white disabled:opacity-30" aria-label="Sposta giù" data-testid={`${kind}-down-${i}`}><ArrowDown className="h-4 w-4" /></button>
        <button onClick={remove} className="p-1.5 text-slate-400 hover:text-red-400" aria-label="Elimina" data-testid={`${kind}-delete-${i}`}><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
    <div className="grid gap-4 md:grid-cols-[1fr_1fr]">
      <div className={`grid gap-3 ${kind === "slide" ? "grid-cols-[1.6fr_1fr]" : ""}`}>
        <ImagePick label="Immagine desktop" url={it.image} onChange={(v) => set({ image: v })} testid={`${kind}-image-${i}`} />
        {kind === "slide" && <ImagePick label="Immagine smartphone (verticale)" url={it.image_mobile} onChange={(v) => set({ image_mobile: v })} testid={`${kind}-image-mobile-${i}`} />}
      </div>
      <div className="space-y-3">
        <input className={input} value={it.title || ""} onChange={(e) => set({ title: e.target.value })} placeholder="Titolo" data-testid={`${kind}-title-${i}`} />
        <input className={input} value={it.subtitle || ""} onChange={(e) => set({ subtitle: e.target.value })} placeholder="Sottotitolo" data-testid={`${kind}-subtitle-${i}`} />
        <div className="grid grid-cols-2 gap-3">
          <input className={input} value={it.button || ""} onChange={(e) => set({ button: e.target.value })} placeholder="Testo pulsante" data-testid={`${kind}-button-${i}`} />
          <input className={input} value={it.link || ""} onChange={(e) => set({ link: e.target.value })} placeholder="/catalogo" data-testid={`${kind}-link-${i}`} />
        </div>
        <div className="grid grid-cols-2 gap-3 text-xs text-slate-400">
          <label>Inquadratura desktop
            <select className={input + " mt-1 bg-[#12141A]"} value={it.position || "center"} onChange={(e) => set({ position: e.target.value })}>{POSITIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
          </label>
          {kind === "slide" && (
            <label>Inquadratura smartphone
              <select className={input + " mt-1 bg-[#12141A]"} value={it.position_mobile || "center top"} onChange={(e) => set({ position_mobile: e.target.value })}>{POSITIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
            </label>
          )}
        </div>
      </div>
    </div>
  </div>
);

export const SlideList = ({ title, kind, items, onChange }) => {
  const set = (i) => (patch) => onChange(items.map((x, k) => (k === i ? { ...x, ...patch } : x)));
  const move = (i) => (d) => {
    const next = [...items];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    onChange(next);
  };
  const add = () => onChange([...items, { id: "", title: "", subtitle: "", button: "", link: "/catalogo", image: null, image_mobile: null, position: "center", position_mobile: "center top", active: false }]);
  return (
    <section data-testid={`${kind}-list`}>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-display text-xl font-semibold">{title}</h2>
        <button onClick={add} className="text-sm text-[#4D94FF]" data-testid={`${kind}-add-btn`}>+ Aggiungi</button>
      </div>
      <div className="space-y-4">
        {items.map((it, i) => <Item key={it.id || i} it={it} i={i} n={items.length} kind={kind} set={set(i)} move={move(i)} remove={() => onChange(items.filter((_, k) => k !== i))} />)}
        {!items.length && <p className="text-sm text-slate-500">Nessun elemento.</p>}
      </div>
      <p className="mt-3 text-xs text-slate-500">Le voci senza immagine o disattivate non vengono mostrate nel sito. Collegamenti: percorsi interni (es. /catalogo?offerte=1) o indirizzi https.</p>
    </section>
  );
};
